import React, { useCallback, useRef, useState } from "react";
import cn from "classnames";
import Draggable from "../../../../Draggable";
import Score from "../../../../Score/score-display";
import Icon from "../../../../../icon";
import { aspectRatio as ar } from "../../../../../../common/parameters";

import { Drag, PlayBarButton, ScorePositioner } from "./styles";
import { IProps } from "./interfaces";
import { convertToNewColors } from "../../../../../utils/convertToNewColors";

const BarDrag = ({
    drag,
    frameScale,
    index,
    solutions,
    color,
    zIndex,
    updateZindex,
    scoreScale,
    drops,
    scorePaddingLeft,
    score,
    scale,
    completed,
    incorrectBars,
    randomOrder,
    playingNote,
    draggedFromDrop,
    onDragStart,
    onStop,
    onDrag,
    transition,
    clickDrag,
    onPlayBar,
    playingBar,
    layout,
    isPlaying,
    isChristmas,
}: IProps) => {
    const [leftOffset, setLeftOffset] = useState(0);
    const [topOffset, setTopOffset] = useState(0);
    const position = useRef<{x: number, y: number}>({x: 0, y: 0})
    const [isTransition, setIsTransition] = useState(true);
    const changeDragPosition = useCallback((left: number, top: number) => {
        if (left + drag.w > ar.width) left = ar.width - drag.w;
        if (top + drag.h > ar.height) top = ar.height - drag.h;
        position.current = {
            x: Math.ceil(left) - drag.l,
            y: Math.ceil(top) - drag.t,
        }
        setLeftOffset(position.current.x)
        setTopOffset(position.current.y)
    }, [setLeftOffset, setTopOffset]);

    const handleDragStart = useCallback(() => {
        if (solutions.indexOf(index) !== -1) {
            draggedFromDrop(index);
        }
        setIsTransition(false);
        onDragStart();
    }, [solutions, draggedFromDrop, setIsTransition, onDragStart ]);

    const handleStop = useCallback(() => {
        setIsTransition(true);
        onDrag(drag.l + position.current.x, drag.t + position.current.y, index);
        setLeftOffset(0);
        setTopOffset(0);
        position.current = {
            x: 0,
            y: 0,
        }
        onStop();
    }, [setIsTransition, onDrag, setLeftOffset, setTopOffset, onStop]);

    const handleUpdateZIndex = useCallback(() => {
        updateZindex(index);
    }, [updateZindex, index]);

    const handleClick = useCallback(() => {
        clickDrag(index);
    }, [clickDrag, index]);

    const handlePlayClick = useCallback(() => {
        onPlayBar(index);
    }, [onPlayBar, index]);

    return (
        <Draggable
            position={{
                x: drag.l + leftOffset,
                y: drag.t + topOffset,
            }}
            scale={frameScale}
            onDrag={changeDragPosition}
            onDragStart={handleDragStart}
            transition={isTransition ? transition : false}
            onStop={handleStop}
            onStart={handleUpdateZIndex}
            zIndex={zIndex}
            onClick={handleClick}
            disabled={completed}
        >
            <Drag
                className={cn(
                    incorrectBars && (incorrectBars.indexOf(index) !== -1 ? "Incorrect" : "Correct"),
                    completed && "HideOnComplete",
                    isPlaying && "Playing",
                    isChristmas && "Christmas"
                )}
                style={{
                    width: drag.w,
                    height: drag.h,
                    ...(!isChristmas && {
                        backgroundColor: convertToNewColors(color),
                    }),
                }}
            >
                <ScorePositioner
                    style={{
                        transform: `scale(${scoreScale}) translateX(${
                            -(drops[randomOrder[index]].l - scorePaddingLeft * scoreScale) / scoreScale
                        }px) translateY(${
                            -drops[randomOrder[index]][layout === "horizontal" ? "t" : "vertTop"] / scoreScale +
                            60 / scoreScale
                        }px)`,
                    }}
                >
                    <Score
                        isSameLineHeight
                        playingNote={playingBar === index ? playingNote : []}
                        score={score}
                        scale={scale}
                        showOnly={{
                            bar: drag.bar,
                            voice: drag.voice,
                        }}
                    />
                </ScorePositioner>
                <PlayBarButton
                    onMouseDown={(event: React.MouseEvent) => event.stopPropagation()}
                    onTouchStart={(event: React.TouchEvent) => event.stopPropagation()}
                    onClick={handlePlayClick}
                >
                    <Icon i="play" />
                </PlayBarButton>
            </Drag>
        </Draggable>
    );
}

export default BarDrag;
